import React, { FC, useMemo } from 'react';
import styled from 'styled-components/native';

import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { useForm } from '@webtrader/core/form';
import i18n, { useTranslation } from '@webtrader/core/i18n';
import { authService, useAuth } from '@webtrader/core/services/auth';
import { useCompany } from '@webtrader/core/services/company';
import { isEmpty, isValidEmail } from '@webtrader/core/validators';
import { validateCompanyAccess } from '@webtrader/core/validators/company';

import { AlertManager } from '@/components/Alert/Manager';
import Button from '@/components/Button';
import TextInput from '@/components/TextInput';

interface IFormValues {
  email?: string;
  metatraderId?: string;
}

interface IAuthPasswordResetFormProps {
  isLive?: boolean;
  onSuccess: () => void;
}

const AuthPasswordResetForm: FC<IAuthPasswordResetFormProps> = ({ isLive = false, onSuccess }) => {
  const { t } = useTranslation();
  const { loadingResetPassword } = useAuth();
  const { company } = useCompany();

  const { hasForgotPasswordAccess } = useMemo(() => {
    return validateCompanyAccess(company);
  }, [company]);

  const { values, setFieldValue, submitForm } = useForm<IFormValues>({
    initialValues: { email: '' },
    onSubmit: async (formValues) => {
      const { email, metatraderId } = formValues;

      if (!email || !isValidEmail(email)) {
        AlertManager().alert({
          message: i18n.t('Please enter a valid email address')
        });

        return;
      }

      const resetPassword = async () => {
        const { success, message } = await authService.resetPassword({ email, metatraderId });

        if (!success) {
          AlertManager().alert({
            message
          });
        } else {
          onSuccess();
        }
      };

      resetPassword();
    }
  });

  const { email, metatraderId } = values;

  const disabledSubmit = useMemo(() => {
    const isCRMCondition = isEmpty(email);
    const isMTCondition = isEmpty(email) || isEmpty(metatraderId);

    return isLive ? isCRMCondition : isMTCondition;
  }, [email, isLive, metatraderId]);

  const onEmailChange = (nextValue: string) => {
    setFieldValue('email', nextValue);
  };

  const onAccountIdChange = (nextValue: string) => {
    setFieldValue('metatraderId', nextValue);
  };

  return (
    <Form>
      <TextInput
        value={email}
        placeholder={t('Enter your email address')}
        keyboardType="email-address"
        onChangeText={onEmailChange}
        testID={EDataTestId.RESET_PASSWORD_EMAIL_INPUT}
      />
      {!isLive && hasForgotPasswordAccess && (
        <TextInput
          onlyNumeric={true}
          value={metatraderId}
          placeholder={t('Enter your account ID')}
          keyboardType="numeric"
          onChangeText={onAccountIdChange}
          testID={EDataTestId.RESET_PASSWORD_ACCCOUNT_ID_INPUT}
        />
      )}
      <FormActions>
        <Button
          disabled={disabledSubmit}
          loading={loadingResetPassword}
          onPress={submitForm}
          testID={EDataTestId.RESET_PASSWORD_SUBMIT}
        >
          {t('SUBMIT')}
        </Button>
      </FormActions>
    </Form>
  );
};

export default AuthPasswordResetForm;

const Form = styled.View`
  gap: 10px;
`;

const FormActions = styled.View`
  padding-top: ${dynamicSize.convertSize(40)}px;
`;
